import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import { supabase } from "../services/supabase";
import LocalStorageUtils from "../utils/localStorage.utils.js";
import apiWithAuth from "../services/apiWithAuth.js";

const AuthContext = createContext(null);

const ensureE164Format = (phone) => {
   if (!phone) return "";

   if (phone.startsWith("+")) return phone;

   const cleanPhone = phone.replace(/\D/g, "");
   if (cleanPhone.length === 10) {
      return `+1${cleanPhone}`;
   }

   if (cleanPhone.length === 11 && cleanPhone.startsWith("1")) {
      return `+${cleanPhone}`;
   }

   return `+${cleanPhone}`;
};

const formatPhoneForDisplay = (phone) => {
   if (!phone) return "";
   const cleanPhone = phone.replace(/^\+1/, "").replace(/\D/g, "");
   if (cleanPhone.length === 10) {
      return `(${cleanPhone.slice(0, 3)}) ${cleanPhone.slice(3, 6)}-${cleanPhone.slice(6)}`;
   }
   return phone;
};

const extractUserData = (supabaseUser) => {
   if (!supabaseUser) return null;

   const metadata = supabaseUser.user_metadata || {};

   const extractedUser = {
      email: supabaseUser.email || metadata.email || "",
      phone: ensureE164Format(supabaseUser.phone || metadata.phone || ""),
      phone_display: formatPhoneForDisplay(supabaseUser.phone || metadata.phone || ""),
      first_name: metadata.first_name || metadata.firstName || "",
      last_name: metadata.last_name || metadata.lastName || "",
      verified: supabaseUser.email_confirmed_at || supabaseUser.phone_confirmed_at,
      user_id: supabaseUser.id,
      cab_card_url: null,
      truck_photo_url: null,
   };

   return extractedUser;
};

export const useAuth = () => {
   const context = useContext(AuthContext);
   if (!context) {
      throw new Error("useAuth must be used within an AuthProvider");
   }
   return context;
};

export const AuthProvider = ({ children }) => {
   const [user, setUser] = useState(null);
   const [isLoading, setIsLoading] = useState(true);
   const [session, setSession] = useState(null);

   const isAuthenticated = !!user;

   useEffect(() => {
      const loadSession = async () => {
         setIsLoading(true);
         try {
            const {
               data: { session: currentSession },
            } = await supabase.auth.getSession();

            if (currentSession) {
               setSession(currentSession);
               await getFullUser(currentSession.user).then(setUser);
            }
         } catch (error) {
            console.error("Session verification failed:", error);
         } finally {
            setIsLoading(false);
         }
      };

      loadSession();

      const {
         data: { subscription },
      } = supabase.auth.onAuthStateChange((event, currentSession) => {
         setSession(currentSession);
         if (currentSession) getFullUser(currentSession.user).then(setUser);
      });

      return () => subscription.unsubscribe();
   }, []);

   const getFullUser = async (user) => {
      const base = extractUserData(user);
      const { data, error } = await supabase
         .from("driver")
         .select("*")
         .eq("user_id", base.user_id)
         .single();
      if (error) throw error;
      return { ...base, ...data };
   };

   const signIn = async (phone, password) => {
      try {
         const { data, error } = await supabase.auth.signInWithPassword({
            phone,
            password,
         });
         if (error) throw error;
         return {
            success: true,
            user: extractUserData(data.user),
            session: data.session,
            message: "Login successful",
         };
      } catch (error) {
         throw error;
      }
   };

   const signUp = async (userData) => {
      try {
         const { data, error } = await supabase.auth.signUp({
            phone: ensureE164Format(userData.phone || ""),
            password: userData.password,
            options: {
               data: {
                  first_name: userData.first_name || "",
                  last_name: userData.last_name || "",
                  email: userData.email || "",
                  display_name: `${userData.first_name || ""} ${userData.last_name || ""}`.trim(),
               },
            },
         });
         if (error) throw error;
         const extractedUser = extractUserData(data.user);

         return {
            success: true,
            user: extractedUser,
            session: data.session,
            message:
               "Account created successfully. Please verify your phone number with the OTP sent via SMS.",
            requires_otp: true,
            otp_sent: true,
         };
      } catch (error) {
         throw error;
      }
   };

   const sendOTP = async (phone) => {
      try {
         const { data, error } = await supabase.auth.signInWithOtp({
            phone,
         });
         if (error) throw error;
         return {
            success: true,
            message: "OTP sent successfully",
         };
      } catch (error) {
         throw error;
      }
   };

   const verifyOTP = async (phone, otpCode, mode = "login") => {
      const { data, error } = await supabase.auth.verifyOtp({
         phone,
         token: otpCode,
         type: "sms",
      });
      if (error) throw error;

      if (mode === "signup") {
         await apiWithAuth.post("/auth/signup", {
            email: data.user.user_metadata.email,
            phone: data.user.phone,
            first_name: data.user.user_metadata.first_name,
            last_name: data.user.user_metadata.last_name,
            user_id: data.user.id,
         });
      }
      return {
         success: true,
         user: extractUserData(data.user),
         session: data.session,
         message: "OTP verification successful",
      };
   };

   const signOut = async () => {
      try {
         await supabase.auth.signOut();
         LocalStorageUtils.clear();
         window.location.assign(window.location.href);
      } catch (error) {
         console.error("Logout error:", error);
         setUser(null);
         LocalStorageUtils.clear();
         window.location.assign(window.location.href);
      }
   };

   const value = useMemo(
      () => ({
         user,
         session,
         isAuthenticated,
         isLoading,
         signIn,
         signUp,
         signOut,
         verifyOTP,
         sendOTP,
      }),
      [user, session, isAuthenticated, isLoading]
   );

   return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
